import { useEffect, useMemo, useRef, useState } from "react";
import {
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
} from "@mui/material";
import { Button } from "react-bootstrap";
import { toast } from "react-toastify";
import styles from "./PricingReview.module.css";
import PricingReview from "./PricingReview";
import ReviewChat from "../MarketPlaceDetail/ReviewChat";
import VendorProfileDialog from "../MarketPlaceDetail/VendorProfileDialog";
import {
  buildFeatureMetaChangeMap,
  buildPlanChangeMap,
  parseChangeJson,
} from "../MarketPlaceDetail/changeHighlighter";
import { pinnedSkipMessage } from "../../../utils/stripeSyncPinned";
import PriceRolloutDialog from "../../Catalog/PriceRolloutDialog";
import ProductMarginControl from "./ProductMarginControl";
import DiscountSplitControl from "./DiscountSplitControl";

/**
 * FULL PRICING & PUBLISH — container
 * ==================================
 * Fetches the submission and owns the publish path. The rendering lives in
 * <PricingReview />; this file exists so that the network + decision code stays
 * small enough to read in one screen.
 *
 * Why this is a NEW file rather than an edit to MarketPlaceDetails
 * ---------------------------------------------------------------
 * MarketPlaceDetails is mounted from three places — this screen, the legacy
 * ProductApproval screen, and AdminProducts. Rewriting it in place would have
 * changed the publish path for all three at once. Instead the approvals screen
 * now mounts this container, and the legacy component is left untouched and
 * still serving its other two callers.
 *
 * The cost of that choice is that handleSubmit / revokeApproval / the confirm
 * dialog below are COPIES. They are deliberately copied verbatim — same
 * endpoints, same request bodies, same field names, same conditionals — because
 * the existing-customer propagation options ride on them and a paraphrase is a
 * bug waiting to happen. If you change one, change the other.
 */
function PricingReviewPanel({
  selectedMarketPlace,
  setSelectedMarketPlace,
  view = "pricing",
  approveRequest = 0,
}) {
  const [vendorId, setVendorId] = useState("");
  const [ownerVendorId, setOwnerVendorId] = useState("");
  const [planType, setPlanType] = useState([]);
  const [intervals, setIntervals] = useState([]);
  const [plans, setPlans] = useState([]);
  const [features, setFeatures] = useState([]);
  const [fetchLoader, setFetchLoader] = useState(false);
  const [source, setSource] = useState("submitted");
  const [approvalStatus, setApprovalStatus] = useState(null);
  const [isSomethingChanged, setIsSomethingChanged] = useState(false);
  const [submitLoader, setSubmitLoader] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Phase 4 — the rollout dialog shown between "Approve" and the publish.
  const [rolloutOpen, setRolloutOpen] = useState(false);
  const [confirmType, setConfirmType] = useState(null); // "approve" | "reject"
  const [showWorkspaceDetails, setShowWorkspaceDetails] = useState(false);
  const [approvalState, setApprovalState] = useState({});
  const [vendorData, setVendorData] = useState({});
  // The carve-out in force for this vendor on this product, and the admin's
  // change to it. `productMargin === null` means they typed something unusable
  // and approving must wait.
  const [margin, setMargin] = useState(null);
  const [productMargin, setProductMargin] = useState({ kind: "inherit" });
  // How this vendor's discount is split. null = typed something unusable.
  const [discountSplit, setDiscountSplit] = useState(null);
  // Prices the reviewer is holding back, keyed by staged plan id. Absent =
  // publish, which is what approving a submission has always meant.
  const [priceDecisions, setPriceDecisions] = useState({});

  // Is this submission on the offer model? Decided per plan at submission, so
  // any plan carrying it puts the whole approval on the split.
  const offerModel = useMemo(
    () => (plans || []).some((p) => p?.pricing_model === "offer"),
    [plans],
  );

  // A real tier to show the split against, rather than an invented 100.
  const splitSample = useMemo(() => {
    for (const p of plans || []) {
      for (const u of p?.unit_ids || []) {
        const industry = Number(u?.Industry_price);
        const offer = Number(u?.Vendor_price);
        if (Number.isFinite(industry) && industry > 0 && Number.isFinite(offer)) {
          return { industry, offer };
        }
      }
    }
    return null;
  }, [plans]);

  // A real buyer price from this submission, so the carve-out preview shows a
  // split the reviewer recognises rather than an invented 100. The first
  // priced tier is enough — the rate is the same on all of them.
  const samplePriceForMargin = useMemo(() => {
    for (const p of plans || []) {
      for (const u of p?.unit_ids || []) {
        const n = Number(u?.Marketplace_price);
        if (Number.isFinite(n) && n > 0) return n;
      }
    }
    return null;
  }, [plans]);
  const marginCurrency = useMemo(
    () => (plans || []).find((p) => p?.currency)?.currency || "INR",
    [plans],
  );
  const [changeJson, setChangeJson] = useState({});
  const [changeType, setChangeType] = useState(null);
  // Why the last Approve was refused by the publishing gates (verified / bank
  // account / agreed margin). Shown as a panel, not only a toast, so the admin
  // can read it and act on it.
  const [publishBlocked, setPublishBlocked] = useState(null);

  // Over-cap price moves, computed SERVER-side at submission and carried on
  // change_log.price_cap. A vendor may have a real reason for breaching the
  // ±25%/year cap — an underlying licence cost jumped, the rupee moved — so
  // the submission is accepted and routed here for a human, never auto-rejected.
  const capFindings = useMemo(
    () =>
      Array.isArray(changeJson?.price_cap?.findings)
        ? changeJson.price_cap.findings
        : [],
    [changeJson],
  );

  const [updateExisting, setUpdateExisting] = useState(true);
  const [applyWhen, setApplyWhen] = useState("end_of_current_period");
  const [customDate, setCustomDate] = useState("");

  // The header "Approve…" button asks for the confirm dialog. Handled once per
  // request, and only after the submission has loaded (while loading, the
  // panel renders a spinner instead of the dialog).
  const handledApproveRequest = useRef(0);
  useEffect(() => {
    if (!approveRequest || approveRequest === handledApproveRequest.current) return;
    if (fetchLoader) return;
    handledApproveRequest.current = approveRequest;
    setConfirmType("approve");
    setConfirmOpen(true);
  }, [approveRequest, fetchLoader]);

  const planChanges = useMemo(() => buildPlanChangeMap(changeJson), [changeJson]);
  const featureMetaChanges = useMemo(
    () => buildFeatureMetaChangeMap(changeJson),
    [changeJson],
  );

  useEffect(() => {
    let dead = false;
    // Nothing selected (e.g. just closed after approve): nothing to fetch.
    if (!selectedMarketPlace?.approval_id) return undefined;
    async function fetchData() {
      try {
        setFetchLoader(true);
        const res = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/admin/get_vendor_product`,
          {
            method: "POST",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({
              approval_id: selectedMarketPlace.approval_id,
            }),
          },
        );
        const data = await res.json();
        if (dead) return;
        setFetchLoader(false);
        if (data.status === 200) {
          setPlanType(data.planType || []);
          setIntervals(data.intervals || []);
          setPlans(data.plans || []);
          setFeatures(data.features || []);
          setVendorId(data.vendor_id);
          setVendorData(data.vendorData);
          setMargin(data.margin || null);
          setOwnerVendorId(data.owner_vendor);
          setChangeJson(parseChangeJson(data.changeJson));
          setChangeType(data.change_type || null);
          // "published" means the decision is already made and we are looking
          // at the live catalogue, not a pending submission.
          setSource(data.source || "submitted");
          setApprovalStatus(data.approval_status || null);
        } else {
          toast.error("Something went wrong");
        }
      } catch (error) {
        console.log("Error", error);
        if (!dead) setFetchLoader(false);
      }
    }
    fetchData();
    return () => {
      dead = true;
    };
    // eslint-disable-next-line
  }, [isSomethingChanged, selectedMarketPlace.approval_id]);

  /* ── publish ─────────────────────────────────────────────────────────
     COPIED VERBATIM from MarketPlaceDetails.handleSubmit. Do not "tidy". */
  // PRICE ROLLOUT (phase 4). Approving a price change no longer re-prices
  // every active customer: it publishes a new price VERSION, and this is the
  // admin's choice of who moves onto it. Held here so handleSubmit can post it
  // verbatim as `rollout`.
  const handleSubmit = async (rollout = null) => {
    // Publishing only materializes units present in approvalState, and the
    // review editors add a plan there only when the admin edits its price or
    // toggles its flag — so approving a plan the admin never touched used to
    // publish NOTHING for it (port of 9e3a5c9 from MarketPlaceDetails). Seed
    // every submitted plan with its current price here, at submit time, so
    // approvalState itself stays "admin edits only" and the "edited" badge in
    // <PricingReview /> keeps meaning what it says. Admin edits override.
    const publishState = {};
    (plans || []).forEach((p) => {
      const units = Array.isArray(p.unit_ids) ? p.unit_ids : [];
      if (!units.length) return;
      publishState[p.id] = {
        need_easexpense_approval: p.need_easexpense_approval,
        unit_prices: units.map((u) => ({
          id: u.id,
          marketplace_price: Number(u.Marketplace_price ?? 0),
        })),
      };
    });
    Object.assign(publishState, approvalState);
    try {
      setSubmitLoader(true);
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/admin/approve_product`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            skuId: selectedMarketPlace.id,
            vendorId: vendorId,
            ownerVendorId: ownerVendorId,
            approvalState: publishState,
            // Only a price_update touches existing customers; for new listings
            // these are omitted (backend default is a harmless no-op).
            // Phase 4: who the new price version is for. Omitted (server
            // default "new customers only") when the admin was never asked.
            ...(rollout ? { rollout } : {}),
            // The carve-out for this product. "inherit" clears any product
            // deal and falls back to the vendor's standing rate.
            ...(productMargin ? { product_margin: productMargin } : {}),
            // Per-price verdicts. Only the held-back ones travel; everything
            // unmentioned publishes, as it always did.
            ...(Object.keys(priceDecisions).length
              ? {
                  price_decisions: Object.entries(priceDecisions).map(
                    ([id, v]) => ({ id: Number(id), ...v }),
                  ),
                }
              : {}),
            // How their discount is split. Only meaningful on the offer model.
            ...(offerModel && discountSplit
              ? { discount_split: discountSplit }
              : {}),
            ...(changeType === "price_update"
              ? {
                  update_existing: updateExisting,
                  ...(updateExisting
                    ? {
                        apply_when: applyWhen,
                        ...(applyWhen === "custom_date"
                          ? { custom_date: customDate }
                          : {}),
                      }
                    : {}),
                }
              : {}),
          }),
        },
      );
      const data = await res.json();
      if (data.status === 200) {
        const sync = data.stripe_sync;
        if (sync && sync.ok === false) {
          toast.warn(
            "Approved, but re-pricing existing customers failed — check and retry.",
          );
        } else if (sync && sync.skipped) {
          toast.success("Approved. Existing customers left unchanged.");
        } else {
          toast.success("Approved Successfully");
        }
        const pinnedMsg = pinnedSkipMessage(sync);
        if (pinnedMsg) toast.warn(pinnedMsg, { autoClose: 9000 });
        // This approval may have made the product a SHARED listing: approving
        // a second vendor on a vendor-owned product flips it to the house and
        // freezes the plan names. The admin has to be told at the moment they
        // did it, not the next time a rename box is grey.
        if (data.auto_share) {
          toast.info(
            `This product is now shared — ${
              data.auto_share.previousOwnerVendorId
                ? `vendor #${data.auto_share.previousOwnerVendorId}`
                : "the previous owner"
            } keeps selling everything they were selling, and plan names are frozen and governed by Easexpense from now on.`,
            { autoClose: 12000 },
          );
        }
        setSubmitLoader(false);
        setSelectedMarketPlace({});
      } else {
        // Show WHY: the publishing gates (verified / bank / margin) return a
        // reason list; a bare "Failed to save" left the admin guessing.
        const reasons = Array.isArray(data?.data?.blocked) ? data.data.blocked : [];
        if (reasons.length) {
          setPublishBlocked({
            vendorName: data?.data?.vendor_name || "This vendor",
            vendorId: data?.data?.vendor_id,
            reasons,
          });
          toast.error("Not approved — see what's missing at the top of the review.");
        } else {
          toast.error(data?.msg || "Failed to save, try again");
        }
        setSubmitLoader(false);
      }
    } catch (error) {
      console.log("Error", error);
      setSubmitLoader(false);
    }
  };

  /* COPIED VERBATIM from MarketPlaceDetails.revokeApproval. */
  const revokeApproval = async () => {
    try {
      setSubmitLoader(true);
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/admin/reject_product`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            skuId: selectedMarketPlace.id,
            vendorId: vendorId,
          }),
        },
      );
      const data = await res.json();
      if (data.status === 200) {
        toast.success("Revoked Successfully");
        setSubmitLoader(false);
        setIsSomethingChanged(!isSomethingChanged);
      } else {
        toast.error("Failed to save, try again");
        setSubmitLoader(false);
      }
    } catch (error) {
      console.log("Error", error);
      toast.error("Failed to save, try again");
      setSubmitLoader(false);
    }
  };

  if (fetchLoader) {
    return (
      <div style={{ display: "grid", placeItems: "center", minHeight: 220 }}>
        <CircularProgress size={26} />
      </div>
    );
  }

  return (
    <div className={styles.wrap}>
      {publishBlocked && (
        <Alert
          severity="error"
          onClose={() => setPublishBlocked(null)}
          style={{ marginBottom: 16 }}
        >
          <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
            Can't approve yet — {publishBlocked.vendorName} is missing{" "}
            {publishBlocked.reasons.length === 1
              ? "1 thing"
              : `${publishBlocked.reasons.length} things`}
          </Typography>
          <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
            {publishBlocked.reasons.map((r, i) => (
              <li key={i} style={{ fontSize: 13 }}>
                {r}
              </li>
            ))}
          </ul>
          <Typography variant="caption" style={{ display: "block", marginTop: 6 }}>
            Nothing was published. The vendor has been emailed this list. Fix it
            from Vendors
            {publishBlocked.vendorId ? ` → #${publishBlocked.vendorId}` : ""} (or the
            vendor adds it in their portal Settings), then approve again.
          </Typography>
        </Alert>
      )}
      {/* `view` is driven by the screen's own tab strip rather than a second,
          nested one. The component stays mounted across the switch so moving to
          the chat and back does not refetch the submission or discard an
          in-progress price edit. */}
      <div style={{ display: view === "pricing" ? undefined : "none" }}>
        <PricingReview
          planType={planType}
          intervals={intervals}
          plans={plans}
          features={features}
          setPlans={setPlans}
          approvalState={approvalState}
          setApprovalState={setApprovalState}
          vendorId={vendorId}
          ownerVendorId={ownerVendorId}
          changeJson={changeJson}
          planChanges={planChanges}
          featureMetaChanges={featureMetaChanges}
          changeType={changeType}
          source={source}
          approvalStatus={approvalStatus}
          busy={submitLoader}
          onApprove={() => {
            setConfirmType("approve");
            setConfirmOpen(true);
          }}
          onReject={() => {
            setConfirmType("reject");
            setConfirmOpen(true);
          }}
          onShowVendor={() => setShowWorkspaceDetails(true)}
          priceDecisions={priceDecisions}
          onPriceDecision={(planId, verdict) =>
            setPriceDecisions((prev) => {
              const next = { ...prev };
              if (!verdict) delete next[planId];
              else next[planId] = verdict;
              return next;
            })
          }
        />
      </div>

      {view === "chat" && (
        <ReviewChat productId={selectedMarketPlace.id} vendorId={vendorId} />
      )}

      {/* ── confirm dialog ────────────────────────────────────────────────
          COPIED VERBATIM from MarketPlaceDetails. This is where the
          existing-customer propagation consent and timing live; the values it
          sets are read by handleSubmit above and posted to approve_product. */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {confirmType === "approve" ? "Confirm Approval" : "Confirm Rejection"}
        </DialogTitle>

        <DialogContent>
          <Typography variant="body2">
            This action is <strong>irreversible</strong>. Are you sure you want
            to {confirmType === "approve" ? "approve" : "reject"} this request?
          </Typography>

          {confirmType === "approve" && capFindings.length > 0 && (
            <Alert severity="warning" style={{ marginTop: 16 }}>
              <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                {capFindings.length} price{capFindings.length > 1 ? "s" : ""}{" "}
                move more than the ±25%/year cap
              </Typography>
              <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                {capFindings.slice(0, 8).map((f, i) => (
                  <li key={i} style={{ fontSize: 13 }}>
                    {f.plan_name ? <strong>{f.plan_name}</strong> : "Tier"}{" "}
                    &mdash; {f.summary}
                  </li>
                ))}
              </ul>
              {capFindings.length > 8 && (
                <Typography variant="caption">
                  …and {capFindings.length - 8} more
                </Typography>
              )}
              <Typography
                variant="caption"
                style={{ display: "block", marginTop: 6 }}
              >
                Not a block — approve it if the vendor has a reason.
              </Typography>
            </Alert>
          )}

          {confirmType === "approve" && offerModel && (
            <DiscountSplitControl
              margin={margin}
              sample={splitSample}
              currency={marginCurrency}
              disabled={submitLoader}
              onChange={setDiscountSplit}
            />
          )}

          {confirmType === "approve" && (
            <ProductMarginControl
              margin={margin}
              samplePrice={samplePriceForMargin}
              currency={marginCurrency}
              disabled={submitLoader}
              onChange={setProductMargin}
            />
          )}

          {confirmType === "approve" && changeType === "price_update" && (
            <div style={{ marginTop: 16 }}>
              <Typography
                variant="subtitle2"
                style={{ fontWeight: 600, marginBottom: 6 }}
              >
                This is a price update
              </Typography>
              <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={updateExisting}
                  onChange={(e) => setUpdateExisting(e.target.checked)}
                />
                <span>Update the price for existing customers too</span>
              </label>
              <Typography
                variant="caption"
                color="textSecondary"
                style={{ display: "block", marginTop: 4 }}
              >
                The new price always applies to new customers. Leave unchecked
                to keep current customers on their existing price.
              </Typography>

              {updateExisting && (
                <div style={{ marginTop: 10 }}>
                  <Typography variant="body2" style={{ marginBottom: 4 }}>
                    When should existing customers move to the new price?
                  </Typography>
                  {[
                    [
                      "end_of_current_period",
                      "At their next renewal (recommended)",
                    ],
                    ["immediate", "Immediately (prorated now)"],
                    ["custom_date", "On a specific date"],
                  ].map(([value, label]) => (
                    <label
                      key={value}
                      style={{ display: "flex", gap: 8, alignItems: "center" }}
                    >
                      <input
                        type="radio"
                        name="apply_when"
                        value={value}
                        checked={applyWhen === value}
                        onChange={(e) => setApplyWhen(e.target.value)}
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                  {applyWhen === "custom_date" && (
                    <input
                      type="date"
                      value={customDate}
                      onChange={(e) => setCustomDate(e.target.value)}
                      style={{ marginTop: 6 }}
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            className={`${styles.act} ${styles.actQuiet}`}
            onClick={() => setConfirmOpen(false)}
            disabled={submitLoader}
          >
            Cancel
          </Button>

          <Button
            className={`${styles.act} ${
              confirmType === "approve"
                ? styles.actGo
                : `${styles.actStop} ${styles.actCommit}`
            }`}
            color={confirmType === "approve" ? "primary" : "error"}
            variant="contained"
            disabled={
              submitLoader ||
              (confirmType === "approve" &&
                changeType === "price_update" &&
                updateExisting &&
                applyWhen === "custom_date" &&
                !customDate) ||
              // A carve-out that is not a usable number must not publish: the
              // control reports null for "typed something, not a number yet".
              (confirmType === "approve" && productMargin === null) ||
              (confirmType === "approve" && offerModel && discountSplit === null)
            }
            onClick={async () => {
              setConfirmOpen(false);

              if (confirmType === "approve") {
                // A price change asks WHO it is for before it publishes.
                // Anything else (a new listing, content) publishes as before.
                if (changeType === "price_update") {
                  setRolloutOpen(true);
                } else {
                  await handleSubmit();
                }
              } else {
                await revokeApproval();
              }
            }}
          >
            {confirmType === "approve" ? "Approve" : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>

      <PriceRolloutDialog
        open={rolloutOpen}
        busy={submitLoader}
        priceIds={(plans || []).map((p) => p.id).filter(Boolean)}
        title="Who is this price for?"
        confirmLabel="Approve & publish"
        onClose={() => setRolloutOpen(false)}
        onConfirm={async (rollout) => {
          setRolloutOpen(false);
          await handleSubmit(rollout);
        }}
      />

      <VendorProfileDialog
        open={showWorkspaceDetails}
        onClose={() => setShowWorkspaceDetails(false)}
        vendor={vendorData}
      />
    </div>
  );
}

export default PricingReviewPanel;
